import { Banner, Button, Field, Input } from '@cloudflare/kumo';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { BRAND_NAME, BRAND_LOGO } from '../lib/brand';
import { TurnstileWidget, useTurnstileSiteKey } from '../components/TurnstileWidget';
import { PasswordField } from '../components/ui/PasswordField';
import { ICONS } from '../components/ui/icons';
import { SkeletonText } from '../components/ui/Skeleton';

interface InvitationPreview {
  email: string;
  role: string;
  roleLabel: string;
  organization: { id: string; name: string };
  invitedBy: string | null;
  expiresAt: string;
  userExists: boolean;
}

const PASSWORD_RULE = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;

/**
 * Aceite de convite (/convite/:token). Funciona com ou sem sessão:
 * - conta nova: nome + senha
 * - conta existente: a senha dela, ou um clique se já estiver logado com o e-mail do convite
 * Ao aceitar, abre a sessão com a organização do convite ativa.
 */
export function InvitePage() {
  const turnstileKey = useTurnstileSiteKey();
  const [captcha, setCaptcha] = useState<string | null>(null);
  const [captchaReset, setCaptchaReset] = useState(0);
  const needCaptcha = !!turnstileKey && !captcha;
  const { token = '' } = useParams();
  const { user, token: sessionToken, logout } = useAuth();
  const [invite, setInvite] = useState<InvitationPreview | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [nome, setNome] = useState('');
  const [senha, setSenha] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetch(`/api/invitations/token/${encodeURIComponent(token)}`)
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.message || 'Convite inválido');
        setInvite(data.invitation);
      })
      .catch((err) => setLoadError(err instanceof Error ? err.message : 'Convite inválido'));
  }, [token]);

  const signedInAsInvitee = !!user && !!invite && user.email.toLowerCase() === invite.email.toLowerCase();
  const signedInAsOther = !!user && !!invite && !signedInAsInvitee;

  const accept = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setError(null);
    if (invite && !invite.userExists && !signedInAsInvitee) {
      if (nome.trim().length < 2) return setError('Informe seu nome');
      if (!PASSWORD_RULE.test(senha)) return setError('A senha precisa de 8 caracteres ou mais, com letra maiúscula, minúscula e número');
    }
    setSubmitting(true);
    try {
      const res = await fetch(`/api/invitations/token/${encodeURIComponent(token)}/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(signedInAsInvitee && sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {}),
        },
        body: JSON.stringify({ ...(signedInAsInvitee ? {} : { nome: nome.trim(), senha }), ...(captcha ? { turnstileToken: captcha } : {}) }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Não foi possível aceitar o convite');
      localStorage.setItem('auth_token', data.data.token);
      localStorage.setItem('selected_tenant_id', data.data.tenantId);
      // Recarrega para os contextos (sessão e organização) partirem do zero
      window.location.assign('/dashboard');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível aceitar o convite');
      setSubmitting(false);
      if (turnstileKey) { setCaptcha(null); setCaptchaReset((n) => n + 1); }
    }
  };

  return (
    <div className="auth-shell">
      <div className="auth-card">
        <div className="auth-brand">
          <img src={BRAND_LOGO} alt={BRAND_NAME} style={{ height: 28, width: 'auto' }} className="[[data-mode=dark]_&]:invert" />
        </div>

        {!invite && !loadError && (
          <div className="py-8"><SkeletonText lines={4} /></div>
        )}

        {loadError && (
          <div>
            <h2 className="text-xl font-semibold auth-title">Convite indisponível</h2>
            <div style={{ color: 'var(--text-muted)', margin: '8px 0 24px' }}>{loadError}</div>
            <Button variant="secondary" onClick={() => window.location.assign('/login')}>Ir para o login</Button>
          </div>
        )}

        {invite && (
          <>
            <h2 className="text-xl font-semibold auth-title">Entrar em {invite.organization.name}</h2>
            <div style={{ color: 'var(--text-muted)', margin: '8px 0 20px' }}>
              {invite.invitedBy ? <><strong>{invite.invitedBy}</strong> convidou </> : 'Você foi convidado, '}
              <strong>{invite.email}</strong> como <strong>{invite.roleLabel}</strong>.
            </div>

            {error && <div className="mb-4"><Banner variant="error" description={<>{error}</>} /></div>}

            {signedInAsOther ? (
              <>
                <div className="mb-4">
                  <Banner variant="alert" description={<>
                    Você está conectado como <strong>{user!.email}</strong>. Este convite é para <strong>{invite.email}</strong>.
                  </>} />
                </div>
                <Button variant="primary" onClick={() => logout()}>Sair e continuar com {invite.email}</Button>
              </>
            ) : signedInAsInvitee ? (
              <>
                {turnstileKey && <TurnstileWidget siteKey={turnstileKey} action="invite" onToken={setCaptcha} resetKey={captchaReset} />}
                <Button variant="primary" className="w-full" loading={submitting} disabled={needCaptcha} onClick={() => accept()}>
                  Aceitar convite
                </Button>
              </>
            ) : (
              <form onSubmit={accept} className="flex flex-col gap-4">
                <Field label="E-mail">
                  <Input  value={invite.email} disabled />
                </Field>
                {!invite.userExists && (
                  <Field label="Seu nome">
                    <Input onChange={(e) => setNome(e.target.value)} value={nome} autoComplete="name" autoFocus />
                  </Field>
                )}
                <PasswordField
                  label={invite.userExists ? 'Sua senha' : 'Crie uma senha'}
                  value={senha}
                  onChange={setSenha}
                  autoComplete={invite.userExists ? 'current-password' : 'new-password'}
                />
                {!invite.userExists && (
                  <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                    8 caracteres ou mais, com maiúscula, minúscula e número
                  </div>
                )}
                {turnstileKey && <TurnstileWidget siteKey={turnstileKey} action="invite" onToken={setCaptcha} resetKey={captchaReset} />}
                <Button type="submit" variant="primary" icon={ICONS.ArrowRight} loading={submitting} disabled={needCaptcha}>
                  {invite.userExists ? 'Entrar e aceitar' : 'Criar conta e aceitar'}
                </Button>
              </form>
            )}
            <div style={{ color: 'var(--text-faint)', fontSize: 12, marginTop: 20 }}>
              Convite válido até {new Date(invite.expiresAt).toLocaleDateString('pt-BR')}.
            </div>
          </>
        )}
      </div>
    </div>
  );
}
